import { useEffect, useState } from "react";
import api, { pctIt } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine, Legend } from "recharts";
import { Flag, CalendarDays, Trophy, TrendingUp } from "lucide-react";

const COL = { rosso: "#EF4444", giallo: "#F59E0B", verde: "#10B981" };
const eur = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const nomeMese = (m) => new Date(`${m}-01`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

function Gauge({ pct, pctArr, colore, testid }) {
  const scala = Math.max(125, pctArr + 5);
  const R = 80, C = 2 * Math.PI * R, ARC = 0.75;
  const len = (p) => (Math.min(p, scala) / scala) * C * ARC;
  const flag = (100 / scala) * 270 - 135;
  return (
    <div className="relative w-48 h-48 sm:w-56 sm:h-56 shrink-0" data-testid={testid}>
    <svg viewBox="0 0 200 200" className="w-full h-full">
      <g transform="rotate(135 100 100)">
        <circle cx="100" cy="100" r={R} fill="none" stroke="#EEF0F3" strokeWidth="16" strokeDasharray={`${C * ARC} ${C}`} strokeLinecap="round" />
        <circle cx="100" cy="100" r={R} fill="none" stroke={COL[colore]} strokeOpacity="0.28" strokeWidth="16" strokeDasharray={`${len(pctArr)} ${C}`} strokeLinecap="round" style={{ transition: "stroke-dasharray 900ms ease" }} />
        <circle cx="100" cy="100" r={R} fill="none" stroke={COL[colore]} strokeWidth="16" strokeDasharray={`${len(pct)} ${C}`} strokeLinecap="round" style={{ transition: "stroke-dasharray 900ms ease" }} />
      </g>
      <g transform={`rotate(${flag} 100 100)`}><line x1="100" y1="8" x2="100" y2="34" stroke="#111" strokeWidth="3" strokeLinecap="round" /></g>
    </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
        <div data-testid={`${testid}-pct`} className="font-display text-3xl sm:text-4xl font-extrabold leading-none">{Math.round(pct)}%</div>
        <div className="text-[11px] text-[var(--muted)] mt-1">del pareggio</div>
        {pctArr > pct && <div className="text-[10px] text-[var(--muted)]">{Math.round(pctArr)}% con in arrivo</div>}
      </div>
    </div>
  );
}

function Riquadro({ titolo, d, soci, children, testid }) {
  return (
    <div data-testid={testid} className={`card-immo p-5 ${d.raggiunto ? "pareggio-ok" : ""}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><Flag size={14} /> {titolo}</div>
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <Gauge pct={d.pct} pctArr={d.pct_con_arrivo} colore={d.colore} testid={`${testid}-gauge`} />
        <div className="min-w-0 flex-1 space-y-2">
          {d.raggiunto ? (
            <div data-testid={`${testid}-raggiunto`} className="font-display text-2xl sm:text-3xl font-extrabold text-emerald-600">Pareggio raggiunto 🎉{soci && <> – ora guadagni: +{eur(d.utile)}</>}</div>
          ) : (
            <div data-testid={`${testid}-mancano`} className="font-display text-2xl sm:text-3xl font-extrabold leading-tight">
              {soci ? <>Mancano {eur(d.mancano)} al pareggio = {d.transazioni_mancanti} transazioni</> : <>Mancano {d.transazioni_mancanti} transazioni al pareggio</>}
            </div>
          )}
          {children}
          {soci && <div className="text-xs text-[var(--muted)] flex flex-wrap gap-x-4 gap-y-1" data-testid={`${testid}-dettaglio`}>
            <span><b className="text-[#111]">Pareggio {eur(d.pareggio)}</b> ({d.pareggio_transazioni} transazioni)</span>
            <span>Incassato {eur(d.incassato)}</span><span>In arrivo {eur(d.in_arrivo)}</span>
            <span>{d.fonte === "banca" ? <>Costi reali {eur(d.costi)} (fisse {eur(d.fisse)} + variabili {eur(d.variabili)})</> : <>Spese fisse previste {eur(d.fisse)}</>}</span>
          </div>}
          {d.raggiunto && d.pct_obiettivo != null && (
            <div data-testid={`${testid}-obiettivo`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold"><Trophy size={13} className="text-amber-500" /> Obiettivo utile{soci && ` ${eur(d.obiettivo_utile)}`}: {Math.round(d.pct_obiettivo)}%</div>
              <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${d.pct_obiettivo}%`, transition: "width 900ms ease" }} /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GraficoAnno({ mesi, soci }) {
  const data = mesi.map((x, i) => ({ n: MESI[i], futuro: x.futuro, v: soci ? x.incassato : x.pct_con_arrivo, s: soci && !x.chiuso && x.fonte !== "banca" ? x.attese_sicure : 0,
    pr: soci && !x.chiuso && x.fonte !== "banca" ? x.attese_probabili : 0, p: soci ? x.pareggio : 100 }));
  return (
    <div className="h-56 mt-4" data-testid="pareggio-anno-grafico">
      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={180}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="n" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 10 }} width={soci ? 56 : 36} tickFormatter={(v) => (soci ? `${Math.round(v / 1000)}k` : `${v}%`)} />
          <Tooltip formatter={(v) => (soci ? eur(v) : `${Math.round(v)}%`)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="v" stackId="a" name={soci ? "Incassate" : "% del pareggio"} fill="#FC82ED">
            {data.map((x, i) => <Cell key={i} fill="#FC82ED" fillOpacity={!soci && x.futuro ? 0.35 : 1} />)}
          </Bar>
          {soci && <Bar dataKey="s" stackId="a" name="Attese sicure" fill="#C026D3" />}
          {soci && <Bar dataKey="pr" stackId="a" name="Attese probabili" fill="#C026D3" fillOpacity={0.3} stroke="#C026D3" strokeDasharray="4 3" radius={[4, 4, 0, 0]} />}
          {soci ? <Bar dataKey="p" name="Pareggio" fill="#9CA3AF" fillOpacity={0.45} radius={[4, 4, 0, 0]} /> : <ReferenceLine y={100} stroke="#111" strokeDasharray="4 3" />}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Prossimi6({ d }) {
  const cur = d.mese.mese;
  const righe = d.mesi.filter((x) => x.mese >= cur).slice(0, 6);
  return (
    <div className="mt-4 overflow-x-auto" data-testid="pareggio-prossimi-6">
      <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1">Prossimi 6 mesi</div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[var(--muted)]"><th className="py-1">Mese</th><th className="text-right">Sicure</th><th className="text-right">Probabili ({pctIt(d.probabile_pct)})</th><th className="text-right">Pareggio previsto</th><th className="text-right">Differenza</th></tr></thead>
        <tbody>{righe.map((x) => (
          <tr key={x.mese} data-testid={`p6-${x.mese}`} className="border-t border-slate-100"><td className="py-1">{nomeMese(x.mese)}</td><td className="text-right mono">{eur(x.attese_sicure)}</td>
            <td className="text-right mono">{eur(x.attese_probabili)}</td><td className="text-right mono">{eur(x.pareggio_previsto)}</td>
            <td className={`text-right mono font-semibold ${x.differenza_attese >= 0 ? "text-emerald-600" : "text-red-600"}`}>{x.differenza_attese >= 0 ? "+" : ""}{eur(x.differenza_attese)}</td></tr>
        ))}</tbody>
      </table>
      <div className="text-[11px] text-[var(--muted)] mt-1" data-testid="pareggio-potenziali">Potenziali (incarichi attivi, solo informativi, non sommati): {eur(d.potenziali_totale)} su {d.potenziali_n} incarichi</div>
      {d.avvisi.length > 0 && <div className="text-[11px] text-amber-700 mt-1" data-testid="pareggio-avvisi">{d.avvisi.map((a, i) => <div key={i}>{a.titolo}: {a.avviso}</div>)}</div>}
    </div>
  );
}

function MiePratiche({ l }) {
  if (!l?.length) return null;
  const T = { sicura: "Sicura", probabile: "Probabile", potenziale: "Potenziale" };
  return (
    <div className="mt-3 text-xs" data-testid="pareggio-mie-pratiche">
      <div className="font-bold uppercase tracking-wider text-[var(--muted)] mb-1">Le mie pratiche attese</div>
      {l.map((p, i) => <div key={i} className="py-0.5">{T[p.tipo]} · {p.titolo}{p.mese ? ` · ${nomeMese(p.mese)}` : ""}{p.avviso ? <span className="text-amber-700"> · {p.avviso}</span> : null}</div>)}
    </div>
  );
}

export function PareggioContatore() {
  const [d, setD] = useState(null);
  const [vista, setVista] = useState("mese");
  useEffect(() => { api.get("/pareggio/contatore").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const soci = d.vista === "soci";
  const m = d.mese, a = d.anno;
  return (
    <section className="mb-6" data-testid="pareggio-contatore">
      <div className="flex items-center gap-2 mb-2">
        {[["mese", `Mese · ${nomeMese(m.mese)}`], ["anno", `Anno ${a.anno}`]].map(([k, l]) => (
          <button key={k} data-testid={`pareggio-tab-${k}`} onClick={() => setVista(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${vista === k ? "bg-[#111] text-white border-[#111]" : "bg-white border-[var(--border)]"}`}>{l}</button>
        ))}
      </div>
      {vista === "mese" ? (
        <Riquadro titolo="Contatore pareggio del mese" d={m} soci={soci} testid="pareggio-mese">
          <div data-testid="pareggio-mese-giorni" className="text-base font-semibold flex items-center gap-1.5"><CalendarDays size={16} /> Giorni rimasti: {m.giorni_rimasti}{!m.raggiunto && m.transazioni_mancanti > 0 && <> → servono {m.giorni_rimasti < 7 ? `${m.transazioni_mancanti} ${m.transazioni_mancanti === 1 ? "transazione" : "transazioni"} in ${m.giorni_rimasti} ${m.giorni_rimasti === 1 ? "giorno" : "giorni"}` : `${m.transazioni_a_settimana} transazioni a settimana`}</>}</div>
        </Riquadro>
      ) : (
        <Riquadro titolo="Contatore pareggio dell'anno" d={a} soci={soci} testid="pareggio-anno">
          {!a.raggiunto && <div data-testid="pareggio-anno-servono" className="text-base font-semibold flex items-center gap-1.5"><TrendingUp size={16} /> Per chiudere l'anno in pareggio servono ancora {soci ? `${eur(a.mancano)} = ` : ""}{a.transazioni_mancanti} transazioni nei {a.mesi_rimasti} mesi rimasti</div>}
          <GraficoAnno mesi={d.mesi} soci={soci} />
          {soci ? <Prossimi6 d={d} /> : <MiePratiche l={d.mie_pratiche} />}
        </Riquadro>
      )}
    </section>
  );
}
